import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Cpu,
  MemoryStick,
  Activity,
  Zap,
  KeyRound,
  Webhook,
  Server,
  Copy,
  Gauge,
  BookOpen,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";

/* ---------------- System load ---------------- */
function SystemLoadCard({ t }) {
  const [load, setLoad] = useState(null);
  const reload = () => api.get("/admin/system/load").then((r) => setLoad(r.data)).catch(() => setLoad(null));
  useEffect(() => {
    reload();
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, []);
  if (!load) return null;
  const heat = (p) => (p >= 85 ? "text-destructive" : p >= 60 ? "text-yellow-600" : "text-primary");
  return (
    <Card className="border-border" data-testid="ent-system-load">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t("ent_system_load")}</h3>
          <span className="text-xs text-muted-foreground">· {t("ent_realtime")}</span>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="label-caps flex items-center gap-1"><Cpu className="h-3 w-3" /> CPU</div>
            <div className={`font-mono-jb text-2xl font-semibold ${heat(load.cpu_percent)}`} data-testid="ent-load-cpu">
              {load.cpu_percent}%
            </div>
            <div className="text-xs text-muted-foreground">{load.cpu_cores} {t("ent_cores")}</div>
          </div>
          <div>
            <div className="label-caps flex items-center gap-1"><MemoryStick className="h-3 w-3" /> RAM</div>
            <div className={`font-mono-jb text-2xl font-semibold ${heat(load.memory_percent)}`} data-testid="ent-load-mem">
              {load.memory_percent}%
            </div>
            <div className="text-xs text-muted-foreground">{load.memory_used_gb} / {load.memory_total_gb} GB</div>
          </div>
          <div>
            <div className="label-caps">Load 1/5/15</div>
            <div className="font-mono-jb text-lg font-semibold">{load.load_avg.join(" · ")}</div>
            <div className="text-xs text-muted-foreground">Linux getloadavg</div>
          </div>
          <div>
            <div className="label-caps">MongoDB</div>
            <div className="font-mono-jb text-lg font-semibold" data-testid="ent-mongo-conn">
              {load.mongo_connections} conn.
            </div>
            <div className="text-xs text-muted-foreground">{load.mongo_ops_per_sec} ops/s ⌀</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Global cache toggle ---------------- */
function CacheCard({ initial, onSaved, t }) {
  const [enabled, setEnabled] = useState(!!initial.global_cache_enabled);
  const [ttl, setTtl] = useState(initial.cache_ttl_seconds ?? 600);
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try {
      await api.patch("/admin/settings", patch);
      toast.success(t("ent_cache_saved"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="border-border" data-testid="ent-cache-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">{t("ent_cache_title")}</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{t("ent_cache_hint")}</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => { setEnabled(v); save({ global_cache_enabled: v }); }}
            data-testid="ent-cache-toggle"
          />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">{t("ent_cache_ttl")}</Label>
          <Input
            type="number"
            min="0"
            step="60"
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
            className="max-w-[120px]"
            data-testid="ent-cache-ttl"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => save({ cache_ttl_seconds: parseInt(ttl, 10) || 0 })}
            disabled={busy}
          >
            {t("save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Metrics per muni ---------------- */
function MetricsMini({ muniId, t }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get(`/admin/metrics/${muniId}?minutes=60`).then((r) => alive && setM(r.data)).catch(() => alive && setM(null));
    load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, [muniId]);
  if (!m) return <div className="text-xs text-muted-foreground">{t("no_data")}</div>;
  return (
    <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
      <div>
        <div className="label-caps">{t("ent_metric_requests")}</div>
        <div className="font-mono-jb text-base font-semibold" data-testid={`metric-requests-${muniId}`}>{m.requests}</div>
      </div>
      <div>
        <div className="label-caps">p50 · p95</div>
        <div className="font-mono-jb text-base font-semibold">{m.p50_ms} · {m.p95_ms} ms</div>
      </div>
      <div>
        <div className="label-caps">{t("ent_metric_max")}</div>
        <div className="font-mono-jb text-base font-semibold">{m.max_ms} ms</div>
      </div>
      <div>
        <div className="label-caps">{t("ent_metric_errors")}</div>
        <div className={`font-mono-jb text-base font-semibold ${m.errors ? "text-destructive" : "text-primary"}`}>
          {m.errors} · {(m.error_rate * 100).toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

/* ---------------- API keys panel ---------------- */
function ApiKeysPanel({ muniId, t }) {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [rateLimit, setRateLimit] = useState(60);
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState(null);

  const reload = () => api.get(`/admin/municipalities/${muniId}/api-keys`).then((r) => setKeys(r.data)).catch(() => setKeys([]));
  useEffect(() => { reload(); }, [muniId]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await api.post(`/admin/municipalities/${muniId}/api-keys`, {
        name: newName.trim(),
        rate_limit_per_min: parseInt(rateLimit, 10) || 60,
      });
      setFreshKey(r.data);
      setNewName("");
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!confirm(t("ent_confirm_revoke"))) return;
    try {
      await api.delete(`/admin/municipalities/${muniId}/api-keys/${id}`);
      toast.success(t("ent_key_revoked"));
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-3" data-testid={`api-keys-panel-${muniId}`}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs">{t("ent_key_name")}</Label>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Prod Integration"
            data-testid={`api-key-name-${muniId}`}
          />
        </div>
        <div className="w-24">
          <Label className="text-xs">{t("ent_rate_limit_min")}</Label>
          <Input
            type="number"
            min="1"
            value={rateLimit}
            onChange={(e) => setRateLimit(e.target.value)}
            data-testid={`api-key-rate-${muniId}`}
          />
        </div>
        <Button size="sm" onClick={create} disabled={creating || !newName.trim()} data-testid={`api-key-create-${muniId}`}>
          <KeyRound className="mr-1 h-3.5 w-3.5" />{t("ent_generate_key")}
        </Button>
      </div>

      <div className="rounded-md border border-border">
        {keys.length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground">{t("ent_keys_empty")}</div>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`api-key-row-${k.id}`}>
                <div>
                  <div className="flex items-center gap-2 font-semibold">
                    {k.name}
                    {k.disabled && <Badge variant="destructive" className="text-xs">{t("ent_disabled")}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-mono-jb">{k.prefix}…</span>
                    {" · "}{k.rate_limit_per_min}/min
                    {" · "}{k.last_used ? formatDateTime(k.last_used) : t("ent_never_used")}
                  </div>
                </div>
                {!k.disabled && (
                  <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} data-testid={`api-key-revoke-${k.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!freshKey} onOpenChange={(v) => !v && setFreshKey(null)}>
        <DialogContent data-testid="fresh-api-key-dialog">
          <DialogHeader>
            <DialogTitle>{t("ent_new_key_title")}</DialogTitle>
            <DialogDescription>{t("ent_new_key_hint")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-muted/40 p-3 font-mono-jb text-xs break-all" data-testid="fresh-api-key-clear">
            {freshKey?.key}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(freshKey.key); toast.success(t("ent_copied")); }}
            >
              <Copy className="mr-1 h-3.5 w-3.5" />{t("ent_copy")}
            </Button>
            <Button onClick={() => setFreshKey(null)}>{t("ent_key_saved_close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Webhook panel ---------------- */
const EVENT_CHOICES = ["booking.approved", "booking.rejected", "booking.completed"];

function WebhookPanel({ muniId, t }) {
  const [w, setW] = useState({ url: "", events: [], enabled: false, secret_set: false, recent_deliveries: [] });
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = () => api.get(`/admin/municipalities/${muniId}/webhook`).then((r) => setW(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [muniId]);

  const toggleEvent = (ev) => {
    setW((prev) => ({
      ...prev,
      events: prev.events.includes(ev) ? prev.events.filter((e) => e !== ev) : [...prev.events, ev],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { url: w.url, events: w.events, enabled: w.enabled };
      if (secret.trim()) body.secret = secret.trim();
      await api.patch(`/admin/municipalities/${muniId}/webhook`, body);
      toast.success(t("ent_webhook_saved"));
      setSecret("");
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`webhook-panel-${muniId}`}>
      <div>
        <Label className="text-xs">{t("ent_webhook_url")}</Label>
        <Input
          value={w.url}
          onChange={(e) => setW({ ...w, url: e.target.value })}
          placeholder="https://api.versorger.de/waterplanner"
          data-testid={`webhook-url-${muniId}`}
        />
      </div>
      <div>
        <Label className="text-xs">{t("ent_webhook_events")}</Label>
        <div className="mt-1 flex flex-wrap gap-2">
          {EVENT_CHOICES.map((ev) => (
            <label
              key={ev}
              className={`cursor-pointer rounded-md border px-2 py-1 text-xs ${w.events.includes(ev) ? "border-primary bg-primary/10 text-primary" : "border-border"}`}
            >
              <input
                type="checkbox"
                className="mr-1"
                checked={w.events.includes(ev)}
                onChange={() => toggleEvent(ev)}
              />
              {ev}
            </label>
          ))}
        </div>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <div>
          <Label className="text-xs">
            {t("ent_webhook_secret")}
            {w.secret_set && <span className="ml-2 text-muted-foreground">({t("key_set_hint")})</span>}
          </Label>
          <Input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={w.secret_set ? "•••••" : t("ent_webhook_secret_hint")}
            data-testid={`webhook-secret-${muniId}`}
          />
        </div>
        <div className="flex items-end justify-between gap-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={!!w.enabled}
              onCheckedChange={(v) => setW({ ...w, enabled: v })}
              data-testid={`webhook-enabled-${muniId}`}
            />
            {t("ent_webhook_enabled")}
          </label>
          <Button size="sm" onClick={save} disabled={busy} data-testid={`webhook-save-${muniId}`}>
            {t("save")}
          </Button>
        </div>
      </div>

      {w.recent_deliveries?.length > 0 && (
        <div className="rounded-md border border-border">
          <div className="label-caps px-3 py-2">{t("ent_webhook_deliveries")}</div>
          <ul className="divide-y divide-border text-xs">
            {w.recent_deliveries.slice(0, 8).map((d, i) => (
              <li key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
                <span className="font-mono-jb">{d.event}</span>
                <span className="text-muted-foreground">{formatDateTime(d.created_at)}</span>
                <span className={d.status_code && d.status_code < 400 ? "text-primary" : "text-destructive"}>
                  {d.error ? "ERR" : d.status_code || "…"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------------- Server resources + scale/capacity request ---------------- */
function ServerResourcesPanel({ muni, onChanged, t }) {
  const [cpu, setCpu] = useState(muni.cpu_cores || 0);
  const [ram, setRam] = useState(muni.ram_gb || 0);
  const [reason, setReason] = useState("");
  const [extras, setExtras] = useState(1);
  const [busy, setBusy] = useState(false);

  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/municipalities/${muni.id}/flags`, {
        cpu_cores: parseInt(cpu, 10) || 0,
        ram_gb: parseInt(ram, 10) || 0,
      });
      toast.success(t("flags_saved"));
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const scaleRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/municipalities/${muni.id}/scale-request`, {
        cpu_cores: parseInt(cpu, 10) || 1,
        ram_gb: parseInt(ram, 10) || 1,
        reason: reason.trim() || undefined,
      });
      toast.success(t("ent_scale_sent"));
      setReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const capacityRequest = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/municipalities/${muni.id}/capacity-request`, {
        extra_instances: parseInt(extras, 10) || 1,
        reason: reason.trim() || undefined,
      });
      toast.success(t("ent_capacity_sent"));
      setReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-2 md:grid-cols-3">
        <div>
          <Label className="text-xs">{t("ent_cpu_cores")}</Label>
          <Input type="number" min="0" value={cpu} onChange={(e) => setCpu(e.target.value)} data-testid={`ent-cpu-${muni.id}`} />
        </div>
        <div>
          <Label className="text-xs">{t("ent_ram_gb")}</Label>
          <Input type="number" min="0" value={ram} onChange={(e) => setRam(e.target.value)} data-testid={`ent-ram-${muni.id}`} />
        </div>
        <div className="flex items-end">
          <Button size="sm" variant="outline" onClick={saveConfig} disabled={busy}>{t("save")}</Button>
        </div>
      </div>

      <div>
        <Label className="text-xs">{t("ent_reason_label")}</Label>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("ent_reason_placeholder")} />
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        <Button size="sm" onClick={scaleRequest} disabled={busy} data-testid={`ent-scale-${muni.id}`}>
          <Server className="mr-1 h-3.5 w-3.5" />{t("ent_scale_send")}
        </Button>
        <div className="flex gap-2">
          <Input
            type="number"
            min="1"
            value={extras}
            onChange={(e) => setExtras(e.target.value)}
            className="max-w-[100px]"
            data-testid={`ent-extra-${muni.id}`}
          />
          <Button size="sm" variant="outline" onClick={capacityRequest} disabled={busy} data-testid={`ent-capacity-${muni.id}`}>
            <Gauge className="mr-1 h-3.5 w-3.5" />{t("ent_capacity_send")}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-amber-500/40 bg-amber-500/[0.06] p-2 text-xs text-amber-900 dark:text-amber-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>{t("ent_scale_disclaimer")}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Per-muni enterprise card ---------------- */
function MunicipalityEnterpriseCard({ muni, onChanged, t }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="border-border" data-testid={`ent-muni-card-${muni.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">{muni.name}</h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{muni.billing_mode || "credit"}</Badge>
              {muni.cpu_cores > 0 && <span>{muni.cpu_cores} vCPU</span>}
              {muni.ram_gb > 0 && <span>{muni.ram_gb} GB</span>}
              <span>{muni.api_keys_count || 0} API-Keys</span>
              <span>{muni.webhook_configured ? t("ent_webhook_on") : t("ent_webhook_off")}</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid={`ent-toggle-${muni.id}`}>
            {open ? t("ent_collapse") : t("ent_open")}
          </Button>
        </div>

        {open && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <section className="space-y-3">
              <h4 className="label-caps flex items-center gap-1"><Server className="h-3 w-3" /> {t("ent_server_scaling")}</h4>
              <ServerResourcesPanel muni={muni} onChanged={onChanged} t={t} />
            </section>

            <section className="space-y-3">
              <h4 className="label-caps flex items-center gap-1"><Gauge className="h-3 w-3" /> {t("ent_metrics_title")}</h4>
              <MetricsMini muniId={muni.id} t={t} />
            </section>

            <section className="space-y-3 lg:col-span-2">
              <h4 className="label-caps flex items-center gap-1"><KeyRound className="h-3 w-3" /> {t("ent_api_keys")}</h4>
              <ApiKeysPanel muniId={muni.id} t={t} />
            </section>

            <section className="space-y-3 lg:col-span-2">
              <h4 className="label-caps flex items-center gap-1"><Webhook className="h-3 w-3" /> {t("ent_webhooks")}</h4>
              <WebhookPanel muniId={muni.id} t={t} />
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------- Endpoint docs ---------------- */
function EndpointDocsCard({ t }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const rows = [
    {
      method: "GET",
      path: "/api/v1/forecast?days=14",
      desc: t("ent_endpoint_forecast_desc"),
      returns: "{ municipality_id, quota_m3_per_day, window, daily:[{date,booked_m3,bookings}] }",
    },
    {
      method: "GET",
      path: "/api/v1/bookings?status=approved&limit=200",
      desc: t("ent_endpoint_bookings_desc"),
      returns: "{ items:[{id,date,status,volume_m3,user_id}], count }",
    },
  ];
  return (
    <Card className="border-border" data-testid="ent-endpoint-docs">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">{t("ent_endpoint_docs")}</h3>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t("ent_endpoint_docs_hint")}</p>
        <div className="mb-3 rounded-md border border-border bg-muted/40 p-3 font-mono-jb text-xs">
          <div>curl -H "X-API-Key: wp_..." \</div>
          <div>&nbsp;&nbsp;&nbsp;{backend}/api/v1/forecast?days=14</div>
        </div>
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.path} className="rounded-md border border-border p-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="rounded bg-primary/10 px-2 py-0.5 font-mono-jb text-xs text-primary">{r.method}</span>
                <span className="font-mono-jb">{r.path}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{r.desc}</p>
              <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 font-mono-jb text-[11px]">
                {r.returns}
              </pre>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- Main panel ---------------- */
export default function EnterprisePanel({ munis, settings, onReload }) {
  const { t } = useI18n();
  if (!munis || !settings) return null;
  return (
    <div className="space-y-6" data-testid="enterprise-panel">
      <div className="grid gap-4 md:grid-cols-2">
        <SystemLoadCard t={t} />
        <CacheCard initial={settings} onSaved={onReload} t={t} />
      </div>
      <EndpointDocsCard t={t} />
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t("ent_customer_management")}</h2>
        {munis.map((m) => (
          <MunicipalityEnterpriseCard key={m.id} muni={m} onChanged={onReload} t={t} />
        ))}
      </div>
    </div>
  );
}
